// Packages
import React from 'react';
import debounce from 'lodash/debounce';

// Services
import { apiFetchData } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import runAfterNextPaint from '@PROJECT_ROOT/assets/responsive-frontend/js/services/runAfterNextPaint';
import { IS_FRAME_SERVICE, setWindowParentLocation } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import LocalStorageManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/LocalStorageManipulationService';

// Parts
import Carousel from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/carousel';
import WishlistWidget from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/WishlistWidget';

// Icons
import {
    MdOutlinePercent,
    MdStar,
    MdArrowDropDown,
    MdArrowDropUp,
    MdWater,
    MdOutlineEditCalendar,
    MdArrowRight,
    MdOutlineStraighten,
    MdOutlineShower,
    MdBalcony,
    MdOutlineSell,
    MdOutlinePersonOutline,
    MdOutlineDoorFront,
} from 'react-icons/md';
import {
    dispatchTrackingEvent,
    resetEcommerceTrackingContext,
    buildBaseAnalyticsItem,
    SEARCH_RESULTS_CARD_EMPTY_SPACE_LINK,
    SEARCH_RESULTS_FACILITY_CLICK,
    SEARCH_RESULTS_FACILITY_IMAGE_LINK,
    SEARCH_RESULTS_FACILITY_TITLE_LINK,
    SEARCH_RESULTS_UNIT_CTA_LINK,
    SEARCH_RESULTS_UNIT_IMAGE_LINK,
    SEARCH_RESULTS_UNIT_TITLE_LINK,
    SELECT_ITEM,
    SEARCH_RESULTS_GALLERY_CHANGE_IMAGE,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';

export default class SearchResultsCard extends React.Component {
    constructor(props) {
        super(props);
        const { data, trackingData } = this.props;
        this.showPrice = !data.showGuestAdvice;
        this.numberOfDisplayedUnits = 2;
        this.lastDisplayedUnitIndex = this.numberOfDisplayedUnits - 1;
        this.breakpointMdPx = 992;
        this.indexOnPage = trackingData?.indexOnPage;
        this.unitCount = data?.units?.length ?? 1;
        // Logika prebačena iz lifecycle metode "componentDidMount" zbog toga da bi galerija bila dostupna i na SSR-u.
        let gallery = [];
        let unitIdToFirstGalleryImageIndexMapper = {};
        if (data && data.gallery && data.units) {
            gallery = [...data.gallery.items];
            unitIdToFirstGalleryImageIndexMapper = {};
            data.units.forEach((unit) => {
                unitIdToFirstGalleryImageIndexMapper[unit.unitId] = gallery.length;
                const unitImages = unit.gallery.items.map((image) => ({
                    ...image,
                    title: `${unit.type.toUpperCase()} ${unit.title}`,
                    unitId: unit.unitId,
                }));
                gallery.push(...unitImages);
            });
        }
        this.state = {
            gallery,
            selectedUnitId: null,
            selectedUnitIndex: 0,
            isGalleryLoading: false,
            isGalleryLoaded: false,
            showAllUnits: false,
            unitIdToFirstGalleryImageIndexMapper,
            galleryIndexToJumpTo: null,
            anchorTagTarget: null,
            anchorTagRel: null,
            pageOnSearchResults: 1,
            disableGalleryTransition: false,
        };
        this.handleUnitSelect = this.handleUnitSelect.bind(this);
        this.debouncedHandleUnitSelect = debounce((unitId) => {
            this.handleUnitSelect(unitId);
        }, 300);
        this.handleUnitHover = this.handleUnitHover.bind(this);
        this.debouncedHandleUnitHover = debounce((unitId) => {
            this.handleUnitHover(unitId);
        }, 300);
    }

    componentDidMount() {
        const { smallDevice } = this.props;
        this.setState({ anchorTagTarget: !smallDevice ? '_blank' : null, anchorTagRel: !smallDevice ? 'noreferrer' : null });
    }

    componentDidUpdate(prevProps) {
        const { smallDevice, trackingData } = this.props;
        if (smallDevice !== prevProps.smallDevice) {
            this.setState({ anchorTagTarget: !smallDevice ? '_blank' : null, anchorTagRel: !smallDevice ? 'noreferrer' : null });
        }
        if (trackingData?.page !== prevProps.trackingData?.page) {
            this.setState({ pageOnSearchResults: trackingData?.page ?? null });
        }
    }

    onCurrentGalleryIndexChanged = async (index) => {
        const { gallery } = this.state;
        const { data } = this.props;
        const selectedUnitId = gallery[index].unitId ?? null;
        const selectedUnitIndex = selectedUnitId !== null ? data.units.findIndex((unit) => unit.unitId === selectedUnitId) : 0;
        this.setState({ selectedUnitId, selectedUnitIndex });
    };

    jumpToGallerySection = (moveDirection) => {
        const { gallery, unitIdToFirstGalleryImageIndexMapper, selectedUnitId } = this.state;
        const { data } = this.props;
        let indexOffset = 0;
        if (moveDirection) {
            indexOffset = moveDirection === 'next' ? 1 : -1;
        }
        const galleryLength = gallery.length;
        const nextIndex = ((selectedUnitId ? unitIdToFirstGalleryImageIndexMapper[selectedUnitId] + indexOffset : indexOffset)
                            + galleryLength) % galleryLength;
        const newSelectedUnitId = gallery[nextIndex].unitId ?? null;
        const newSelectedUnitIndex = selectedUnitId !== null ? data.units.findIndex((unit) => unit.unitId === selectedUnitId) : 0;
        this.setState({ galleryIndexToJumpTo: nextIndex, selectedUnitId: newSelectedUnitId, selectedUnitIndex: newSelectedUnitIndex }, () => {
            this.setState({ galleryIndexToJumpTo: null });
        });
    };

    handleShowMoreUnits = (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.setState((prevState) => ({
            showAllUnits: !prevState.showAllUnits,
        }));
    };

    getAdditionalData = (moveDirection) => {
        const { gallery, isGalleryLoaded, isGalleryLoading } = this.state;
        if (isGalleryLoaded || isGalleryLoading) return;
        const { data } = this.props;
        this.setState({ isGalleryLoading: true });
        apiFetchData('accommodation/private/search/facility-gallery/', `/${data.facilityId}`)
            .then((res) => {
                if (res.status !== 200) {
                    reportError(res);
                    return;
                }
                const newFacilityImages = res.data.facilityGallery?.flatMap((item) => item.images) ?? [];
                const newGallery = [gallery[0], ...newFacilityImages];
                const unitIdToFirstGalleryImageIndexMapper = {};
                const { unitsGallery } = res.data;
                unitsGallery.forEach((unitGallery) => {
                    if (!data.units.some((unit) => unit.unitId === unitGallery.unitId)) return;
                    unitIdToFirstGalleryImageIndexMapper[unitGallery.unitId] = newGallery.length;
                    const newUnitImages = unitGallery.images?.map(
                        (image) => ({ ...image, title: unitGallery.title, unitId: unitGallery.unitId }),
                    ) ?? [];
                    newGallery.push(
                        gallery.find((image) => image.unitId === unitGallery.unitId),
                        ...newUnitImages,
                    );
                });
                this.setState({
                    gallery: newGallery,
                    unitIdToFirstGalleryImageIndexMapper,
                    isGalleryLoaded: true,
                    isGalleryLoading: false,
                }, () => {
                    this.jumpToGallerySection(moveDirection);
                });
            })
            .catch((err) => reportError(err))
            .finally(() => this.setState({ isGalleryLoading: false }));
    };

    handleFacilityClickTrackingEvents = (redirectUrl, zeroUnitIndex = false) => {
        if (!IS_FRAME_SERVICE) {
            dispatchTrackingEvent(SEARCH_RESULTS_FACILITY_CLICK);
            resetEcommerceTrackingContext();
            const { data } = this.props;
            const { selectedUnitId, pageOnSearchResults } = this.state;
            const selectedUnit = data.units.find((unit) => unit.unitId === selectedUnitId) || data.units[0]; // fallback je prva jedinica (klik na sliku ili prazan prostor na kartici .)
            const selectedUnitIndex = data.units.findIndex((unit) => unit.unitId === selectedUnitId);
            const finalUnitIndex = selectedUnitIndex ? selectedUnitIndex + 1 : 1;
            const baseAnalyticsItem = buildBaseAnalyticsItem({ unitItem: selectedUnit });
            const item = {
                ...baseAnalyticsItem,
                currency: 'EUR',
                index: this.indexOnPage ? this.indexOnPage + 1 : 1,
                // TODO: Uklonit? Nisam pronašao slucaj u kojem data sadži ove podatke, tako da uvik završi na hardcode.
                item_category: data?.categoryName || 'Rezultati pretrage',
                item_list_id: data?.categoryId || 'search',
                item_list_name: data?.categorySlug || 'Kategorija Rezultati pretrage',
                unit_count: this.unitCount,
                unit_index: zeroUnitIndex ? 0 : finalUnitIndex,
                page: pageOnSearchResults ?? 1,
            };

            dispatchTrackingEvent(SELECT_ITEM, {
                ecommerce: {
                    item_list_id: data?.categoryId || 'search',
                    item_list_name: data?.categorySlug || 'Kategorija Rezultati pretrage',
                    items: [item],
                },
            });
            LocalStorageManipulationService.setTrackingItem(item);
        }
        if (redirectUrl) {
            if (IS_FRAME_SERVICE) {
                setWindowParentLocation(redirectUrl);
            } else {
                window.location = redirectUrl;
            }
        }
    };

    handleUnitClick = (e, unitId) => {
        e.preventDefault();
        const { data } = this.props;
        const { selectedUnitId } = this.state;
        const { parentElement } = e.currentTarget;
        const cursorXPosition = e.clientX;
        const cursorYPosition = e.clientY;
        const clickedElements = document.elementsFromPoint(cursorXPosition, cursorYPosition);
        const isClickOnCTA = clickedElements.some((element) => (
            parentElement.contains(element) && !!element.closest('.card-unit__cta')
        ));
        const isClickOnContent = !isClickOnCTA;
        const desktopArrowElement = parentElement.querySelector('.card-unit__cta');
        const desktopArrowRect = desktopArrowElement.getBoundingClientRect();
        const isMobileView = window.innerWidth < this.breakpointMdPx;
        const newSelectedUnitIndex = selectedUnitId !== null ? data.units.findIndex((unit) => unit.unitId === unitId) : 0;
        const redirectUrl = e.currentTarget?.href;
        if (isMobileView) {
            const isClickOnSelectedUnit = selectedUnitId === unitId;
            if (isClickOnCTA) {
                this.handleFacilityClickTrackingEvents(redirectUrl);
                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_UNIT_CTA_LINK);
                window.location.href = redirectUrl;

                return;
            }
            if (isClickOnSelectedUnit && isClickOnContent) {
                this.handleFacilityClickTrackingEvents(redirectUrl);
                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_UNIT_TITLE_LINK);
                window.location.href = redirectUrl;

                return;
            }
            if (!isClickOnSelectedUnit) {
                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_UNIT_TITLE_LINK);
                runAfterNextPaint(() => {
                    this.setState({ selectedUnitId: unitId, selectedUnitIndex: newSelectedUnitIndex }, () => {
                        this.jumpToGallerySection();
                    });
                });
            }
        } else {
            this.handleFacilityClickTrackingEvents();
            if (cursorXPosition >= desktopArrowRect.left && cursorXPosition <= desktopArrowRect.right
                && cursorYPosition >= desktopArrowRect.top && cursorYPosition <= desktopArrowRect.bottom) {
                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_UNIT_CTA_LINK);
            } else if (!IS_FRAME_SERVICE) {
                dispatchTrackingEvent(SEARCH_RESULTS_UNIT_TITLE_LINK);
            }
            this.setState({ selectedUnitId: unitId, selectedUnitIndex: newSelectedUnitIndex }, () => {
                this.jumpToGallerySection();
            });
            const newTabOpeningCheck = window.open(redirectUrl, '_blank');
            // Otvaranje u istom tabu ako iz kojeg razloga nije uspijelo u novom
            if (!newTabOpeningCheck || newTabOpeningCheck.closed || typeof newTabOpeningCheck.closed === 'undefined') {
                window.location.href = redirectUrl;
            }
        }
    };

    handleOnMouseLeave = () => {
        const { selectedUnitId } = this.state;
        if (!selectedUnitId) return;
        this.setState({ selectedUnitId: null, selectedUnitIndex: 0 }, () => {
            this.jumpToGallerySection();
        });
    };

    handleUnitSelect = (unitId) => {
        const { data } = this.props;
        if (window.innerWidth < this.breakpointMdPx) return;
        const newSelectedUnitIndex = unitId !== null ? data.units.findIndex((unit) => unit.unitId === unitId) : 0;
        this.setState({ selectedUnitId: unitId, selectedUnitIndex: newSelectedUnitIndex }, () => {
            this.jumpToGallerySection();
        });
    };

    handleUnitHover = (unitId) => {
        const { data } = this.props;

        if (window.innerWidth < this.breakpointMdPx) return;

        const newSelectedUnitIndex = unitId !== null
            ? data.units.findIndex((unit) => unit.unitId === unitId)
            : 0;

        this.setState({
            disableGalleryTransition: true,
            selectedUnitId: unitId,
            selectedUnitIndex: newSelectedUnitIndex,
        }, () => {
            this.jumpToGallerySection();

            window.setTimeout(() => {
                this.setState({
                    disableGalleryTransition: false,
                });
            }, 50);
        });
    };

    handleImageClickTrackingEvents = () => {
        const { selectedUnitId } = this.state;
        this.handleFacilityClickTrackingEvents();
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(selectedUnitId === null ? SEARCH_RESULTS_FACILITY_IMAGE_LINK : SEARCH_RESULTS_UNIT_IMAGE_LINK);
    };

    handleTitleClickTrackingEvents = () => {
        this.handleFacilityClickTrackingEvents();
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_FACILITY_TITLE_LINK);
    };

    handleEmptySpaceClickTrackingEvents = () => {
        this.handleFacilityClickTrackingEvents(null, true);
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_CARD_EMPTY_SPACE_LINK);
    };

    getRemainingUnitsCount = () => {
        const { data } = this.props;
        if (!data?.units?.length || typeof this.numberOfDisplayedUnits !== 'number') {
            return null;
        }
        const remaining = data.units.length - this.numberOfDisplayedUnits;

        return remaining > 0 ? remaining : null;
    };

    renderImageSourceElements = (item, index) => {
        const { bigimg } = this.props;
        if (!item.imageSourceURLLargeSizeAlternative) {
            return null;
        }

        return (
            <source
                srcSet={bigimg || index !== 0 ? `
                    ${item.image?.sourceURL} 365w,
                    ${item.imageSourceURLLargeSizeAlternative} 1275w
                ` : `
                    ${item.image?.sourceURL} 365w
                `}
                sizes="
                    (max-width: 991px) 92vw,
                    365px
                "
            />
        );
    };

    render() {
        const {
            data,
            firstCarouselImageLazy,
            isForMap,
            bigimg,
            smallDevice,
            wishlistedUnits,
            updateWishlistedUnits,
            wishlistModalData,
            openLoginModal,
            missingSearchCardAlertData,
            firstCarouselImageLcpPriority,
        } = this.props;
        const {
            showAllUnits,
            gallery,
            isGalleryLoaded,
            isGalleryLoading,
            selectedUnitId,
            galleryIndexToJumpTo,
            selectedUnitIndex,
            anchorTagTarget,
            anchorTagRel,
            disableGalleryTransition,
        } = this.state;
        const imageWidth = isForMap ? 300 : 360;
        const imageHeight = isForMap ? 200 : 240;
        const remainingUnitsCount = this.getRemainingUnitsCount();
        const mainURL = data.units[selectedUnitIndex].attractiveOffer?.reservationURL ?? data.units[selectedUnitIndex].resultUnitURL;

        return data && (
            <div
                className="search-results-card"
                onMouseLeave={this.handleOnMouseLeave}
            >
                <a
                    className="card__main-link"
                    href={mainURL}
                    aria-label={data.units[selectedUnitIndex].fullTitle}
                    target={anchorTagTarget}
                    rel={anchorTagRel}
                    onClick={this.handleEmptySpaceClickTrackingEvents}
                />
                <div
                    role="presentation"
                    className={`card__img-container ${disableGalleryTransition ? 'card__img-container--no-transition' : ''}`}
                >
                    {gallery.length > 0 && (
                        <Carousel
                            anchorTagUrl={mainURL}
                            anchorTagLabel={data.units[selectedUnitIndex].fullTitle}
                            onImageClick={this.handleImageClickTrackingEvents}
                            data={{ items: gallery, id: data.facilityId }}
                            isGalleryLoaded={isGalleryLoaded}
                            autoplay={false}
                            usedOnMap={isForMap}
                            imageWidth={imageWidth}
                            imageHeight={imageHeight}
                            imageSourceElements={this.renderImageSourceElements}
                            customObjectFit="cover"
                            firstImageLazy={firstCarouselImageLazy}
                            galleryFetching={isGalleryLoading}
                            onCurrentIndexChanged={this.onCurrentGalleryIndexChanged}
                            fetchGallery={this.getAdditionalData}
                            indexToJumpTo={galleryIndexToJumpTo}
                            maxHeight={bigimg ? null : imageHeight * 1.3}
                            isMobileScreen={smallDevice}
                            trackingEvents={{
                                searchResultsGalleryChangeImage: {
                                    eventName: SEARCH_RESULTS_GALLERY_CHANGE_IMAGE,
                                },
                            }}
                            imagePriorityLCP={firstCarouselImageLcpPriority}
                        />
                    )}
                    {data.hasActiveDiscount && (
                        <div className="search-result-card__discount">
                            <span className="search-result-card__discount-text">{data.discountPercentText || <MdOutlinePercent className="search-result-card__discount-svg" />}</span>
                        </div>
                    )}
                    {!IS_FRAME_SERVICE && wishlistModalData && (
                        <WishlistWidget
                            unitsData={data.units}
                            wishlistedUnits={wishlistedUnits}
                            outsideUpdateWishlistedUnits={updateWishlistedUnits}
                            openLoginModal={openLoginModal}
                        />
                    )}
                </div>
                <div className="card__info">
                    <div className="card__info-header">
                        <h2 className="card__header-title">
                            <a
                                className="card__header-title-link"
                                href={mainURL}
                                onClick={this.handleTitleClickTrackingEvents}
                                target={anchorTagTarget}
                                rel={anchorTagRel}
                            >
                                <span>{data.genericTitle}</span>
                                <span className="card__header-subtitle">{data.locationAndFacilityId}</span>
                            </a>
                        </h2>
                        {data.rating && (
                            <span className="card__header-rating">
                                <MdStar className="card__header-rating-icon" />
                                <span>{data.rating}</span>
                            </span>
                        )}
                    </div>
                    {(data.distaceToBeach || data.freeCancel) && (
                        <div className="card__info-additional">
                            {data.distaceToBeach && (
                                <p className="card__info-additional-item">
                                    <MdWater className="card__info-additional-item-icon" />
                                    <span>{data.distaceToBeach}</span>
                                </p>
                            )}
                            {data.freeCancel && (
                                <p className="card__info-additional-item">
                                    <MdOutlineEditCalendar className="card__info-additional-item-icon" />
                                    <span>{data.freeCancel}</span>
                                </p>
                            )}
                        </div>
                    )}
                    <div className="card__info-content">
                        <div className="card__info-content-header">
                            <span>{data.accommodationUnitsText}</span>
                            {data.units?.length > 0 && (
                                <span>{data.numberOfUnitsText}<span> {data.units.length}</span></span>
                            )}
                        </div>
                        <div className="card__info-content-units">
                            {data.units.map((unit, unitIndex) => {
                                const priceWithoutDiscount = unit.attractiveOffer?.priceWithoutDiscount
                                    ?? unit.fullFlexibleIntervalOffer?.priceWithoutDiscount
                                    ?? unit.priceTextWithoutDiscount;
                                const priceWithDiscount = unit.attractiveOffer?.priceWithDiscount
                                    ?? unit.fullFlexibleIntervalOffer?.priceWithDiscount
                                    ?? unit.priceText;
                                const shouldShowPrice = (this.showPrice && priceWithDiscount) || unit.attractiveOffer;
                                return (
                                    <div
                                        className={`search-result-card__unit ${unit.unitId === selectedUnitId ? 'search-result-card__unit--selected' : ''}${!showAllUnits && unitIndex > this.lastDisplayedUnitIndex ? 'd-none' : ''}`}
                                        key={`unit-details-${unit.unitId}`}
                                        data-id={unit.unitId}
                                    >
                                        <a
                                            href={unit.attractiveOffer?.reservationURL ?? unit.resultUnitURL}
                                            aria-label={unit.fullTitle}
                                            rel="noreferrer"
                                            className="card__unit-link"
                                            onMouseEnter={() => this.debouncedHandleUnitHover(unit.unitId)}
                                            onFocus={() => this.debouncedHandleUnitSelect(unit.unitId)}
                                            onClick={(e) => this.handleUnitClick(e, unit.unitId)}
                                            data-js-target="_blank"
                                        >
                                            {/* Zbog SEO <a> element mora imati tocni anchor tekst */}
                                            <span className="sr-only">{unitIndex < 1 ? unit.fullTitle : `${unit.type} ${unit.title}`}</span>
                                        </a>
                                        <div className="card-unit__content-upper">
                                            <div className="card-unit__content">
                                                <div className="card-unit__content-main">
                                                    <div className="card-unit__content-main-header">
                                                        <div className="card-unit__content-main-title-wrap">
                                                            <span className="card-unit__content-main-title" title={`${unit.type} ${unit.title}`}>
                                                                <span className="text-uppercase">{unit.type}</span>
                                                                <span> {unit.title}</span>
                                                            </span>
                                                            {unit.deviationInterval && (
                                                                <span className="card-unit__content-main-deviation-interval">{unit.deviationInterval}</span>
                                                            )}
                                                            {unit.fullFlexibleIntervalOffer && (
                                                                <span className="card-unit__content-main-deviation-interval">{unit.fullFlexibleIntervalOffer.interval}</span>
                                                            )}
                                                            {unit.attractiveOffer && (
                                                                <span className="card-unit__attractive-offer-chip">
                                                                    <span className="card-unit__attractive-offer-chip-interval">
                                                                        {unit.attractiveOffer.interval}
                                                                    </span>
                                                                    <span className="card-unit__attractive-offer-chip-percent">
                                                                        {unit.attractiveOffer.discountPercent}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Dupliciran element "card-unit__content-price" zbog logike dizajna desktop/mobile. */}
                                                        {shouldShowPrice && (
                                                            <div className="card-unit__content-price card-unit__content-price--show-mob">
                                                                {priceWithoutDiscount ? (
                                                                    <span
                                                                        className="card-unit__content-price-without-discount"
                                                                        dangerouslySetInnerHTML={{ __html: priceWithoutDiscount }}
                                                                    />
                                                                ) : (
                                                                    <span className="card-unit__content-price-without-discount card-unit__content-price-without-discount--hidden">0</span>
                                                                )}
                                                                <span
                                                                    className="card-unit__content-price-txt"
                                                                    dangerouslySetInnerHTML={{ __html: priceWithDiscount }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="card-unit__main-details">
                                                        <span className="card-unit__main-details-item">
                                                            <MdOutlineStraighten className="card-unit__main-details-item-icon" />
                                                            {unit.spaceIndoor ? (
                                                                <span
                                                                    key={`unit-space-indor-${unit.unitId}`}
                                                                    dangerouslySetInnerHTML={{ __html: unit.spaceIndoor }}
                                                                />
                                                            ) : (
                                                                <span
                                                                    key={`unit-space-${unit.unitId}`}
                                                                    dangerouslySetInnerHTML={{ __html: unit.space }}
                                                                />
                                                            )}
                                                        </span>
                                                        {unit.spaceOutdoor && (
                                                            <span className="card-unit__main-details-item">
                                                                <MdBalcony className="card-unit__main-details-item-icon" />
                                                                <span dangerouslySetInnerHTML={{ __html: unit.spaceOutdoor }} />
                                                            </span>
                                                        )}
                                                        <span className="card-unit__main-details-item">
                                                            <MdOutlineDoorFront className="card-unit__main-details-item-icon" />
                                                            <span>{unit.rooms}</span>
                                                        </span>
                                                        <span className="card-unit__main-details-item">
                                                            <MdOutlineShower className="card-unit__main-details-item-icon" />
                                                            <span>{unit.bathrooms}</span>
                                                        </span>
                                                        <span className="card-unit__main-details-item">
                                                            <MdOutlinePersonOutline className="card-unit__main-details-item-icon" />
                                                            <span>{unit.people}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Dupliciran element "card-unit__content-price" zbog logike dizajna desktop/mobile. */}
                                                {shouldShowPrice && (
                                                    <div className="card-unit__content-price card-unit__content-price--hide-mob">
                                                        {priceWithoutDiscount && (
                                                            <span className="card-unit__content-price-without-discount" dangerouslySetInnerHTML={{ __html: priceWithoutDiscount }} />
                                                        )}
                                                        <span className="card-unit__content-price-txt" dangerouslySetInnerHTML={{ __html: priceWithDiscount }} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="card-unit__cta">
                                                <span className="card-unit__cta-txt">{unit.attractiveOffer?.callToActionString ?? data.selectText}</span>
                                                <MdArrowRight className="card-unit__cta-icon" />
                                            </div>
                                        </div>
                                        {unit.attractiveOffer && (
                                            <div className="card-unit__content-savings">
                                                <MdOutlineSell className="card-unit__content-savings-icon" />
                                                <span className="card-unit__content-savings-text">{unit.attractiveOffer.callToActionString}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* Else na klasi postoji samo da bi se ispunio zahtjev dizajna(samo na mobilnom view-u) sa istim prostorom i kad nema više jedinica */}
                    <div className="card__info-show-btn-wrap">
                        <button
                            className={`${data.units?.length > this.numberOfDisplayedUnits ? 'card__info-show-btn' : 'card__info-show-btn--show-mobile-space'}`}
                            type="button"
                            onClick={this.handleShowMoreUnits}
                        >
                            {showAllUnits ? (
                                <span>
                                    <span>{data.showLessUnitsText}</span>
                                    <MdArrowDropUp className="card__info-show-btn-icon" />
                                </span>
                            ) : (
                                <span>
                                    <span>
                                        <span>{data.showAllUnitsText}</span>
                                        {remainingUnitsCount > null && (
                                            <span> (+{remainingUnitsCount})</span>
                                        )}
                                    </span>
                                    <MdArrowDropDown className="card__info-show-btn-icon" />
                                </span>
                            )}
                        </button>
                        {data.showGuestAdvice && missingSearchCardAlertData && (
                            <button
                                className="card__info-show-btn-additional"
                                type="button"
                                onClick={missingSearchCardAlertData.onClick}
                                aria-label={missingSearchCardAlertData.text}
                            >
                                <span
                                    dangerouslySetInnerHTML={{ __html: missingSearchCardAlertData.text }}
                                />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
